import React, { useState, useEffect, useCallback } from 'react';
import { Grid, CircularProgress, Box } from '@material-ui/core';
import { loadSimovsatCharts } from '~/services/api';
import { SimovsatChartsResponse } from '~/types';
import { handleApiErrorResponse } from '~/services/handleErrors';
import Snackbar from '~/components/Snackbar/index';
import { SeverityType } from '~/components/Snackbar/index';
import ActiveInactiveCard from './ActiveInactiveCard';
import AlertsCard from './AlertsCard';
import VehicleCard from './VehicleCard';
import ProgressCard from './ProgressCard';
import DistanceChart from './DistanceChart';
import StatusCards from './StatusCards';

export default function Charts() {
  const [loading, setLoading] = useState(true);
  const [chartsData, setChartsData] = useState<SimovsatChartsResponse['data'] | null>(null);
  const [messageSnack, setMessageSnack] = useState('');
  const [severity, setSeverity] = useState<SeverityType>('info');
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const loadCharts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await loadSimovsatCharts();

      if (response.status === 200 && response.data) {
        setChartsData(response.data.data);
      } else {
        setSeverity('warning');
        setMessageSnack('Não foi possível carregar os dados dos gráficos.');
        setOpenSnackbar(true);
      }
    } catch (err) {
      const error = handleApiErrorResponse(err);
      setSeverity('error');
      setMessageSnack(
        (error?.applicationErrorMessage || error?.apiError) +
          ': não foi possível carregar os gráficos. Tente novamente.',
      );
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCharts();
  }, [loadCharts]);

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (!chartsData) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight={400}>
        <p>Não foi possível carregar os dados dos gráficos.</p>
      </Box>
    );
  }

  return (
    <div>
      {/* Cards principais em Grid */}
      <Grid container spacing={3} style={{ marginBottom: 30 }}>
        <Grid item xs={12} sm={6} md={3}>
          <ActiveInactiveCard activeInactive={chartsData.ativo_inativo || { on: { count: 0, percent: 0 }, off: { count: 0, percent: 0 } }} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AlertsCard alerts={chartsData.alertas || []} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <VehicleCard vehicle={chartsData.veiculo || { total: 0, ligado: 0, offline: 0, nunca_conectado: 0, expirado: 0 }} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ProgressCard progress={chartsData.andamento || { total: 0, novo: 0, em_progresso: 0, concluido: 0, falhou: 0 }} />
        </Grid>
      </Grid>

      {/* Cards de Status/Movimentação */}
      <Box style={{ marginBottom: 30 }}>
        <StatusCards statusMovement={chartsData.status_movimentacao || []} />
      </Box>

      {/* Gráfico de Distância Percorrida */}
      <Box style={{ marginBottom: 30 }}>
        <DistanceChart
          distanceByVehicle={chartsData.distancia_percorrida?.por_entidade || []}
        />
      </Box>

      <Snackbar
        message={messageSnack}
        open={openSnackbar}
        severity={severity}
        onClose={() => setOpenSnackbar(false)}
      />
    </div>
  );
}
